import { useEffect, useRef, useState } from 'react'

interface HotkeyModalProps {
  open: boolean
  onClose: () => void
}

type Tab = 'shortcuts' | 'tips'

const SHORTCUTS = [
  { section: 'Navigation' },
  { key: '/',         desc: 'Focus search bar' },
  { key: '1',         desc: 'Terminal' },
  { key: '2',         desc: 'Portfolio' },
  { key: '3',         desc: 'Watchlist' },
  { key: '4',         desc: 'Settings' },
  { key: '5',         desc: 'Sector heatmap' },
  { key: '6',         desc: 'Earnings' },
  { key: '7',         desc: 'Options chain' },
  { key: '8',         desc: 'Simulator' },
  { key: '9',         desc: 'Screener' },
  { key: '0',         desc: 'Equity Research' },
  { key: 'Esc',       desc: 'Blur input / close modals' },
  { section: 'Chart — Period' },
  { key: '[',         desc: 'Step period backward  (1d ← … ← 5y)' },
  { key: ']',         desc: 'Step period forward   (1d → … → 5y)' },
  { section: 'Chart — Interval' },
  { key: '{',         desc: 'Step interval backward  (1m ← … ← 1mo)' },
  { key: '}',         desc: 'Step interval forward   (1m → … → 1mo)' },
  { section: 'Chart — Type' },
  { key: 'C',         desc: 'Candlestick chart' },
  { key: 'L',         desc: 'Line chart' },
  { key: 'A',         desc: 'Area chart' },
  { section: 'Indicators' },
  { key: 'R',         desc: 'Toggle RSI' },
  { key: 'M',         desc: 'Toggle MACD' },
  { key: 'B',         desc: 'Toggle Bollinger Bands' },
  { key: 'V',         desc: 'Toggle Volume' },
  { section: 'Drawing Tools' },
  { key: 'H',         desc: 'Draw horizontal line (toggle)' },
  { key: 'T',         desc: 'Draw trendline (toggle)' },
  { key: 'F',         desc: 'Draw Fibonacci retracement (toggle)' },
  { key: 'X',         desc: 'Clear all drawings for active symbol' },
  { key: 'N',         desc: 'Exit comparison mode' },
  { section: 'Data & Watchlist' },
  { key: 'P',         desc: 'Pause / resume live data' },
  { key: 'W',         desc: 'Add / remove active symbol from watchlist' },
  { section: 'General' },
  { key: '?',         desc: 'Toggle this keyboard reference' },
]

const TIPS = [
  'Type any ticker symbol in the search bar — company names work too.',
  'Press / anywhere (outside an input) to instantly focus the search bar.',
  'The W key adds or removes the active symbol from your watchlist without leaving the terminal.',
  'Hold your mouse over any nav tab to see a description of that page.',
  'Use [ and ] to quickly scrub through time periods without clicking.',
  'The sector heatmap highlights your active terminal symbol with a star badge.',
  'Indicators stack below the chart — toggle multiple at once with R, M, B.',
  'All chart drawings persist per symbol — switch tickers and come back to find them.',
  'P pauses live polling so you can study a price level without the chart refreshing.',
  'The Simulator page lets you build a portfolio and get an AI-generated order plan.',
]

export function HotkeyModal({ open, onClose }: HotkeyModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const dialogRef  = useRef<HTMLDivElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const [tab, setTab] = useState<Tab>('shortcuts')

  // Focus trap + return focus on close
  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement as HTMLElement | null
    // Move focus into the dialog
    requestAnimationFrame(() => closeBtnRef.current?.focus())

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === '?') {
        e.preventDefault()
        onClose()
        return
      }
      // Trap Tab inside dialog
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last  = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus()
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => {
      window.removeEventListener('keydown', handler)
      previouslyFocused.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[300] bg-black/70 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === overlayRef.current) onClose() }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bb-hotkey-title"
        className="bg-bb-surface border border-bb-border rounded-lg shadow-2xl w-full max-w-md overflow-hidden"
      >

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-bb-border">
          <span id="bb-hotkey-title" className="text-xs font-semibold text-bb-orange tracking-wider">HELP & SHORTCUTS</span>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close help"
            className="text-bb-disabled hover:text-bb-text text-lg leading-none min-w-[44px] min-h-[44px] flex items-center justify-center rounded transition-colors"
          >×</button>
        </div>

        {/* Tabs */}
        <div role="tablist" aria-label="Help sections" className="flex border-b border-bb-border">
          {(['shortcuts', 'tips'] as Tab[]).map((t) => (
            <button
              key={t}
              role="tab"
              id={`bb-hotkey-tab-${t}`}
              aria-selected={tab === t}
              aria-controls={`bb-hotkey-panel-${t}`}
              tabIndex={tab === t ? 0 : -1}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-xs font-medium transition-colors capitalize ${
                tab === t
                  ? 'text-bb-orange border-b border-bb-orange -mb-px'
                  : 'text-bb-disabled hover:text-bb-subtle'
              }`}
            >
              {t === 'shortcuts' ? 'Keyboard Shortcuts' : 'Tips & Tricks'}
            </button>
          ))}
        </div>

        {/* Shortcuts tab */}
        {tab === 'shortcuts' && (
          <div
            role="tabpanel"
            id="bb-hotkey-panel-shortcuts"
            aria-labelledby="bb-hotkey-tab-shortcuts"
            className="p-4 space-y-1 max-h-[65vh] overflow-y-auto"
          >
            {SHORTCUTS.map((item, i) => (
              'section' in item ? (
                <p key={i} className="text-2xs text-bb-disabled uppercase tracking-wider pt-3 pb-1 first:pt-0">
                  {item.section}
                </p>
              ) : (
                <div key={i} className="flex items-center gap-3 py-0.5">
                  <kbd className="shrink-0 bg-bb-bg border border-bb-border-2 rounded px-2 py-0.5 font-mono text-bb-orange text-2xs min-w-[2rem] text-center shadow-[0_1px_0_var(--bb-border-2)]">
                    {item.key}
                  </kbd>
                  <span className="text-xs text-bb-text">{item.desc}</span>
                </div>
              )
            ))}
          </div>
        )}

        {/* Tips tab */}
        {tab === 'tips' && (
          <div
            role="tabpanel"
            id="bb-hotkey-panel-tips"
            aria-labelledby="bb-hotkey-tab-tips"
            className="p-4 space-y-3 max-h-[65vh] overflow-y-auto"
          >
            {TIPS.map((tip, i) => (
              <div key={i} className="flex gap-3">
                <span className="shrink-0 text-bb-orange font-mono text-xs mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-xs text-bb-subtle leading-relaxed">{tip}</p>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-bb-border text-center">
          <span className="text-2xs text-bb-disabled">
            Press <kbd className="font-mono text-bb-orange">?</kbd> or <kbd className="font-mono text-bb-orange">Esc</kbd> to close
          </span>
        </div>
      </div>
    </div>
  )
}
